import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { AnalyticsOverview } from '../types/billing.ts';
import api from '../services/api.ts';

const Analytics: React.FC = () => {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, [selectedTimeframe]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.analytics.getOverview();
      setOverview(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError('Failed to load analytics data. Please check if the API server is running.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900 border border-red-700 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-lg font-medium text-red-400">Error Loading Analytics</h3>
            <p className="text-red-300 mt-1">{error}</p>
            <button 
              onClick={loadAnalytics}
              className="mt-3 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-0">Advanced Analytics</h1>
        <div className="flex items-center space-x-4">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <div className="text-sm text-gray-400">
            Updated: {overview ? new Date(overview.timestamp).toLocaleTimeString() : ''}
          </div>
        </div>
      </div>

      {/* System Health */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">System Health</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${overview?.system_health.database_connected ? 'bg-green-400' : 'bg-red-400'}`}></div>
            <span className="text-white">Database</span>
            <span className={`text-sm ${overview?.system_health.database_connected ? 'text-green-400' : 'text-red-400'}`}>
              {overview?.system_health.database_connected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${overview?.system_health.prometheus_available ? 'bg-green-400' : 'bg-red-400'}`}></div>
            <span className="text-white">Prometheus</span>
            <span className={`text-sm ${overview?.system_health.prometheus_available ? 'text-green-400' : 'text-red-400'}`}>
              {overview?.system_health.prometheus_available ? 'Available' : 'Unavailable'}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${overview?.system_health.background_tasks_running ? 'bg-green-400' : 'bg-red-400'}`}></div>
            <span className="text-white">Background Tasks</span>
            <span className={`text-sm ${overview?.system_health.background_tasks_running ? 'text-green-400' : 'text-red-400'}`}>
              {overview?.system_health.background_tasks_running ? 'Running' : 'Stopped'}
            </span>
          </div>
        </div>
      </div>

      {/* Key Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-2">Total Projects</h3>
          <p className="text-2xl font-bold text-blue-400">{overview?.project_stats.total_projects}</p>
          <div className="text-sm text-gray-400 mt-1">
            <span className="text-green-400">{overview?.project_stats.active_projects} active</span>
            {' • '}
            <span className="text-red-400">{overview?.project_stats.inactive_projects} inactive</span>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-2">Total Users</h3>
          <p className="text-2xl font-bold text-green-400">{overview?.user_stats.total_users}</p>
          <div className="text-sm text-gray-400 mt-1">
            <span className="text-green-400">{overview?.user_stats.active_users} active</span>
            {' • '}
            <span className="text-blue-400">{overview?.user_stats.recent_registrations} recent</span>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-2">Avg Cost/User</h3>
          <p className="text-2xl font-bold text-yellow-400">
            ${overview?.cost_overview.average_cost_per_user.toFixed(2)}
          </p>
          <div className="text-sm text-gray-400 mt-1">
            ${overview?.cost_overview.average_cost_per_project.toFixed(2)} per project
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h3 className="text-lg font-medium text-white mb-2">High Utilization</h3>
          <p className="text-2xl font-bold text-red-400">{overview?.credit_insights.users_over_90_percent}</p>
          <div className="text-sm text-gray-400 mt-1">
            <span className="text-yellow-400">{overview?.credit_insights.users_over_75_percent}</span> over 75%
          </div>
        </div>
      </div>

      {/* Service Breakdown */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Service Cost Breakdown</h2>
        {overview?.cost_overview.service_breakdown && overview.cost_overview.service_breakdown.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={overview.cost_overview.service_breakdown} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="service" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                formatter={(value: any, name: string) => [
                  name === 'cost' ? `$${Number(value).toFixed(2)}` : 
                  name === 'credits' ? `${Number(value).toFixed(2)} credits` :
                  name === 'total_units' ? `${Number(value).toFixed(2)} units` :
                  name === 'percentage' ? `${Number(value).toFixed(1)}%` :
                  value,
                  name === 'cost' ? 'Cost' : 
                  name === 'credits' ? 'Credits' :
                  name === 'total_units' ? 'Total Units' :
                  name === 'percentage' ? 'Percentage' :
                  name
                ]}
              />
              <Bar dataKey="cost" fill="#3B82F6" name="cost" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[400px] text-gray-400">
            No service breakdown data available
          </div>
        )}
      </div>

      {/* Service Details Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Detailed Service Metrics</h2>
        {overview?.cost_overview.service_breakdown && overview.cost_overview.service_breakdown.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 pb-2">Service</th>
                  <th className="text-right text-gray-400 pb-2">Cost (USD)</th>
                  <th className="text-right text-gray-400 pb-2">Credits</th>
                  <th className="text-right text-gray-400 pb-2">Total Units</th>
                  <th className="text-left text-gray-400 pb-2">Unit Type</th>
                  <th className="text-right text-gray-400 pb-2">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {overview.cost_overview.service_breakdown.map((service, index) => (
                  <tr key={service.service} className="border-b border-gray-700 last:border-b-0">
                    <td className="py-3">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        <span className="text-white capitalize">{service.service}</span>
                      </div>
                    </td>
                    <td className="text-right text-white py-3">${service.cost.toFixed(2)}</td>
                    <td className="text-right text-white py-3">{service.credits.toFixed(2)}</td>
                    <td className="text-right text-white py-3">{service.total_units.toLocaleString()}</td>
                    <td className="text-left text-gray-400 py-3">{service.unit_of_measure}</td>
                    <td className="text-right text-white py-3">{service.percentage.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            No service metrics available
          </div>
        )}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Credit Utilization */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Credit Utilization</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Overall Utilization</span>
              <span className="text-2xl font-bold text-white">
                {overview?.credit_insights.overall_utilization_percentage.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3">
              <div 
                className="bg-blue-400 h-3 rounded-full transition-all duration-300" 
                style={{ width: `${overview?.credit_insights.overall_utilization_percentage || 0}%` }}
              ></div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-sm text-gray-400">Credits Consumed</p>
                <p className="text-lg font-bold text-red-400">
                  {overview?.credit_insights.total_credits_consumed.toFixed(0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Credits Remaining</p>
                <p className="text-lg font-bold text-green-400">
                  {overview?.credit_insights.total_credits_remaining.toFixed(0)}
                </p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-700">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Users &gt;75% utilized:</span>
                <span className="text-yellow-400">{overview?.credit_insights.users_over_75_percent}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-400">Users &gt; 90% utilized:</span>
                <span className="text-red-400">{overview?.credit_insights.users_over_90_percent}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Summary */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Cost Summary</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400">Monthly Total</p>
              <p className="text-2xl font-bold text-white">
                ${overview?.cost_overview.monthly_total.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Total User Cost</p>
              <p className="text-lg font-bold text-blue-400">
                ${overview?.cost_overview.total_user_cost.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Credits Consumed</p>
              <p className="text-lg font-bold text-green-400">
                {overview?.cost_overview.total_credits_consumed.toFixed(2)}
              </p>
            </div>
            <div className="pt-4 border-t border-gray-700">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Average per User:</span>
                <span className="text-white">${overview?.cost_overview.average_cost_per_user.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-400">Average per Project:</span>
                <span className="text-white">${overview?.cost_overview.average_cost_per_project.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Trends */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Monthly Cost Trends</h2>
        {overview?.cost_overview.trends && overview.cost_overview.trends.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={overview.cost_overview.trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                formatter={(value: any) => [`$${Number(value).toFixed(2)}`, 'Cost']}
              />
              <Line type="monotone" dataKey="total_cost" stroke="#3B82F6" strokeWidth={2} name="Total Cost" />
              <Line type="monotone" dataKey="total_user_cost" stroke="#10B981" strokeWidth={2} name="User Cost" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[300px] text-gray-400">
            No trend data available
          </div>
        )}
      </div>

      {/* User and Project Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Users by Projects */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Top Users by Projects</h2>
          {overview?.user_stats.top_users_by_projects && overview.user_stats.top_users_by_projects.length > 0 ? (
            <div className="space-y-3">
              {overview.user_stats.top_users_by_projects.map((user, index) => (
                <div key={user.email} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-400 font-medium">#{index + 1}</span>
                    <div>
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-sm text-gray-400">{user.email}</p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-blue-400">{user.project_count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              No user project data available
            </div>
          )}
        </div>

        {/* Project Distribution */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white mb-4">Project Distribution</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Projects with Users</span>
              <div className="flex items-center space-x-2">
                <div className="w-32 bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-green-400 h-2 rounded-full" 
                    style={{ 
                      width: `${overview?.project_stats.total_projects ? 
                        (overview.project_stats.projects_with_users / overview.project_stats.total_projects * 100) : 0}%` 
                    }}
                  ></div>
                </div>
                <span className="text-white font-medium">
                  {overview?.project_stats.projects_with_users}
                </span>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Projects without Users</span>
              <div className="flex items-center space-x-2">
                <div className="w-32 bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-red-400 h-2 rounded-full" 
                    style={{ 
                      width: `${overview?.project_stats.total_projects ? 
                        (overview.project_stats.projects_without_users / overview.project_stats.total_projects * 100) : 0}%` 
                    }}
                  ></div>
                </div>
                <span className="text-white font-medium">
                  {overview?.project_stats.projects_without_users}
                </span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-700">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Users with Projects:</span>
                <span className="text-white">{overview?.user_stats.users_with_projects}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-400">Users without Projects:</span>
                <span className="text-white">{overview?.user_stats.users_without_projects}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;